import { ClientPage } from "../../submodules/rapdv/client/elements/PagesCtrl"
import { MessageToast } from "../elements/MessageToast"

export class PageEditArticle implements ClientPage {
  private static UPLOAD_URL = "/api/v1/photo"
  private static UPLOAD_FIELD_NAME = "file"
  private static CSRF_HEADER = "X-CSRF-Token"
  private static CSRF_INPUT_SELECTOR = 'input[name="_csrf"]'
  private static CONTENT_SELECTOR = 'textarea[name="content"]'
  private static DRAG_OVER_CLASS = "border-primary"
  private static IMAGE_MIME_PREFIX = "image/"
  private static FILES_TRANSFER_TYPE = "Files"
  private static LINE_BREAK = "\n"
  private static NOT_FOUND_INDEX = -1
  private static FILE_EXTENSION_REGEX = /\.[^.]+$/
  private static WORD_SEPARATORS_REGEX = /[-_]+/g
  private static MARKDOWN_BRACKETS_REGEX = /[\[\]]/g
  private static UPLOAD_FAILED_TITLE = "Image upload failed"
  private static UPLOAD_FAILED_MESSAGE = "Couldn't upload image"

  private contentTextarea: HTMLTextAreaElement
  private uploadCounter = 0

  getPageId = (): string => "edit-article"

  execute = (): void => {
    this.contentTextarea = document.querySelector<HTMLTextAreaElement>(PageEditArticle.CONTENT_SELECTOR)
    if (!this.contentTextarea) return

    this.contentTextarea.addEventListener("dragover", this.onDragOver)
    this.contentTextarea.addEventListener("dragleave", this.onDragLeave)
    this.contentTextarea.addEventListener("drop", this.onDrop)
  }

  onPageClose = (): void => {
    if (!this.contentTextarea) return
    this.contentTextarea.removeEventListener("dragover", this.onDragOver)
    this.contentTextarea.removeEventListener("dragleave", this.onDragLeave)
    this.contentTextarea.removeEventListener("drop", this.onDrop)
  }

  private onDragOver = (event: DragEvent): void => {
    if (!this.hasFiles(event)) return
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
    this.contentTextarea.classList.add(PageEditArticle.DRAG_OVER_CLASS)
  }

  private onDragLeave = (): void => {
    this.contentTextarea.classList.remove(PageEditArticle.DRAG_OVER_CLASS)
  }

  private onDrop = async (event: DragEvent): Promise<void> => {
    this.contentTextarea.classList.remove(PageEditArticle.DRAG_OVER_CLASS)
    if (!this.hasFiles(event)) return
    event.preventDefault()

    const images = Array.from(event.dataTransfer.files).filter((file) => file.type.startsWith(PageEditArticle.IMAGE_MIME_PREFIX))
    if (images.length === 0) return

    this.contentTextarea.focus()
    for (const image of images) {
      await this.uploadImage(image)
    }
  }

  private hasFiles = (event: DragEvent): boolean => {
    return Array.from(event.dataTransfer?.types ?? []).includes(PageEditArticle.FILES_TRANSFER_TYPE)
  }

  private uploadImage = async (image: File): Promise<void> => {
    const uploadId = ++this.uploadCounter
    const placeholder = `![Uploading ${image.name}...](uploading-${uploadId})`
    this.insertAtCursor(placeholder)

    try {
      const key = await this.sendImage(image)
      const altText = this.getAltText(image.name)
      this.replaceText(placeholder, `![${altText}](${PageEditArticle.UPLOAD_URL}/${key})`)
    } catch (error) {
      this.replaceText(placeholder, "")
      MessageToast.showError(PageEditArticle.UPLOAD_FAILED_TITLE, `Couldn't upload ${image.name}. ${error?.message ?? ""}`.trim())
    }
  }

  private sendImage = async (image: File): Promise<string> => {
    const formData = new FormData()
    formData.append(PageEditArticle.UPLOAD_FIELD_NAME, image)

    const csrfToken = document.querySelector<HTMLInputElement>(PageEditArticle.CSRF_INPUT_SELECTOR)?.value ?? ""
    const response = await fetch(PageEditArticle.UPLOAD_URL, {
      method: "POST",
      headers: { [PageEditArticle.CSRF_HEADER]: csrfToken },
      body: formData
    })

    const key = (await response.text()).trim()
    if (!response.ok || !key) {
      throw new Error(key || PageEditArticle.UPLOAD_FAILED_MESSAGE)
    }
    return key
  }

  private getAltText = (fileName: string): string => {
    return fileName
      .replace(PageEditArticle.FILE_EXTENSION_REGEX, "")
      .replace(PageEditArticle.WORD_SEPARATORS_REGEX, " ")
      .replace(PageEditArticle.MARKDOWN_BRACKETS_REGEX, "")
      .trim()
  }

  private insertAtCursor = (text: string): void => {
    const textarea = this.contentTextarea
    const selectionStart = textarea.selectionStart ?? textarea.value.length
    const selectionEnd = textarea.selectionEnd ?? selectionStart
    const textBefore = textarea.value.substring(0, selectionStart)
    const textAfter = textarea.value.substring(selectionEnd)

    const leadingBreak = textBefore.length > 0 && !textBefore.endsWith(PageEditArticle.LINE_BREAK) ? PageEditArticle.LINE_BREAK : ""
    const trailingBreak = textAfter.length > 0 && !textAfter.startsWith(PageEditArticle.LINE_BREAK) ? PageEditArticle.LINE_BREAK : ""
    const insertion = leadingBreak + text + trailingBreak

    textarea.value = textBefore + insertion + textAfter
    const cursorPosition = textBefore.length + insertion.length
    textarea.setSelectionRange(cursorPosition, cursorPosition)
    this.notifyContentChanged()
  }

  private replaceText = (search: string, replacement: string): void => {
    const textarea = this.contentTextarea
    const searchPosition = textarea.value.indexOf(search)
    if (searchPosition === PageEditArticle.NOT_FOUND_INDEX) return

    const selectionStart = textarea.selectionStart
    const selectionEnd = textarea.selectionEnd
    textarea.value = textarea.value.substring(0, searchPosition) + replacement + textarea.value.substring(searchPosition + search.length)

    const lengthDifference = replacement.length - search.length
    textarea.setSelectionRange(
      this.shiftSelection(selectionStart, searchPosition, lengthDifference),
      this.shiftSelection(selectionEnd, searchPosition, lengthDifference)
    )
    this.notifyContentChanged()
  }

  private shiftSelection = (index: number, changePosition: number, lengthDifference: number): number => {
    if (index <= changePosition) return index
    return Math.max(changePosition, index + lengthDifference)
  }

  private notifyContentChanged = (): void => {
    this.contentTextarea.dispatchEvent(new Event("input", { bubbles: true }))
  }
}
